<script setup lang="ts">
import { computed } from 'vue'
import { router } from '@inertiajs/vue3'
import { useBrowserLocation } from '@vueuse/core'
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from '@/components/ui/pagination'
import type { IPaginationData, IDataTableUtilityParams } from './types'
import { buildQueryFromObject } from './utils'

const props = defineProps<{
    items: IPaginationData<unknown>
    paramState: IDataTableUtilityParams
}>()

const showingFrom = computed(() => {
    if (props.items.data.length === 0) return 0
    return (props.items.current_page - 1) * props.items.per_page + 1
})

const showingTo = computed(() => {
    return Math.min(
        props.items.current_page * props.items.per_page,
        props.items.total_records
    )
})

const pages = computed(() => {
    const current = props.items.current_page
    const last = props.items.last_page
    const delta = 1

    if (last <= 7) {
        return Array.from({ length: last }, (_, i) => i + 1)
    }

    const range: (number | string)[] = []
    const left = current - delta
    const right = current + delta + 1

    for (let i = 1; i <= last; i++) {
        if (i === 1 || i === last || (i >= left && i < right)) {
            if (range[range.length - 1] !== '...' && i !== 1 && range.length > 0) {
                const lastNum = range[range.length - 1]
                if (typeof lastNum === 'number' && i - lastNum > 1) {
                    range.push('...')
                }
            }
            range.push(i)
        }
    }

    return range
})

const navigateToPage = (page: number) => {
    const currentRoute =
        useBrowserLocation().value.origin +
        (useBrowserLocation().value.pathname ?? '')

    router.get(
        currentRoute + '?' + buildQueryFromObject({ ...props.paramState, current_page: page }),
        undefined,
        {
            preserveState: true,
            preserveScroll: true,
        }
    )
}
</script>

<template>
    <div v-if="items.last_page > 1" class="flex items-center justify-center px-6 py-4">
        <Pagination>
            <PaginationContent>
                <PaginationItem :class="{ 'opacity-0 pointer-events-none': !items.prev_page }">
                    <PaginationPrevious @click="items.prev_page && navigateToPage(items.prev_page)" />
                </PaginationItem>

                <PaginationItem v-for="(page, index) in pages" :key="index">
                    <PaginationEllipsis v-if="page === '...'" />
                    <PaginationLink
                        v-else
                        :is-active="page === items.current_page"
                        @click="page !== items.current_page && navigateToPage(page as number)"
                    >
                        {{ page }}
                    </PaginationLink>
                </PaginationItem>

                <PaginationItem :class="{ 'opacity-0 pointer-events-none': !items.next_page }">
                    <PaginationNext @click="items.next_page && navigateToPage(items.next_page)" />
                </PaginationItem>
            </PaginationContent>
        </Pagination>
    </div>
</template>
